/**
 * exactOptionalPropertyTypes-safe object builder.
 * Omits keys whose values are `undefined` (does not remove `null`).
 */

export type OmitUndefinedProperties<T> = {
  [K in keyof T as undefined extends T[K] ? (T[K] extends undefined ? never : K) : K]: Exclude<
    T[K],
    undefined
  >;
};

export function omitUndefined<T extends Record<string, unknown>>(
  obj: T
): OmitUndefinedProperties<T> {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(obj) as Array<keyof T>) {
    if (obj[key] !== undefined) {
      result[key as string] = obj[key];
    }
  }
  return result as OmitUndefinedProperties<T>;
}

/** noUncheckedIndexedAccess-safe CSS module class merge (exactOptionalPropertyTypes-friendly). */
export function css(...parts: Array<string | undefined>): string {
  return parts.filter((p): p is string => Boolean(p)).join(' ');
}

/** Conditionally spread a single optional field. */
export function opt<K extends string, V>(
  key: K,
  value: V | undefined
): V extends undefined ? Record<string, never> : Record<K, V> {
  return (value === undefined ? {} : { [key]: value }) as V extends undefined
    ? Record<string, never>
    : Record<K, V>;
}
